import gql from "graphql-tag";

var ASSIGNMENTS_QUERY = gql`
    query AccommodationAssignmentsExport($first: Int!, $page: Int!, $entity: String, $campus: String, $semester: String) {
        accommodationAssignments(first: $first, page: $page, entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                id
                entity
                campus
                semester
                villa
                room_no
                bed_slot
                student_id
                sponsor
                date_of_entry
                student {
                    student_id
                    name_en
                    eid
                    program_name
                }
            }
            paginatorInfo {
                currentPage
                lastPage
                total
            }
        }
    }
`;

var SETTINGS_QUERY = gql`
    query AccommodationSettingsExport($entity: String!, $campus: String!, $semester: String!) {
        accommodationSettings(entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                villas_identical
                semester
                total_capacity
                villa_count
                rooms_per_villa
                beds_per_villa
                room_bed_layout
                available_rooms_beds
            }
        }
    }
`;

var SETTINGS_LIST_QUERY = gql`
    query AccommodationSettingsListExport($first: Int!, $page: Int!, $entity: String!, $campus: String!) {
        accommodationSettingsList(first: $first, page: $page, entity: $entity, campus: $campus) {
            error
            message
            data {
                semester
                villas_identical
                total_capacity
                villa_count
                rooms_per_villa
                beds_per_villa
                room_bed_layout
                available_rooms_beds
            }
            paginatorInfo {
                currentPage
                lastPage
                total
            }
        }
    }
`;

function num(value) {
    if (value === null || value === undefined || value === "") return "";
    return value;
}

function studentName(item) {
    if (item && item.student && item.student.name_en) return item.student.name_en;
    return "";
}

function studentEid(item) {
    if (item && item.student && item.student.eid) return item.student.eid;
    return "";
}

function studentProgram(item) {
    if (item && item.student && item.student.program_name) return item.student.program_name;
    return "";
}

function configRowFromSettings(entity, campus, semester, settings) {
    return {
        Entity: entity,
        Campus: campus,
        Semester: semester || (settings && settings.semester) || "",
        "Villas Identical": settings && settings.villas_identical ? "Yes" : "No",
        "Total Capacity": settings ? settings.total_capacity : "",
        "Villa Count": settings ? settings.villa_count : "",
        "Rooms Per Villa": settings && settings.rooms_per_villa ? settings.rooms_per_villa : "",
        "Beds Per Villa": settings && settings.beds_per_villa ? settings.beds_per_villa : "",
        "Room Bed Layout": settings && settings.room_bed_layout ? settings.room_bed_layout : "Custom per villa",
        "Available Rooms/Beds": settings ? settings.available_rooms_beds : "",
    };
}

/**
 * @param {string|null|undefined} semester Empty / null = all semesters for the campus
 */
export function buildAccommodationExportSheets(apollo, entity, campus, semester) {
    var semesterFilter = semester ? String(semester) : "";
    var settingsPromise = semesterFilter
        ? apollo
              .query({
                  query: SETTINGS_QUERY,
                  variables: { entity: entity, campus: campus, semester: semesterFilter },
                  fetchPolicy: "network-only",
              })
              .then(function (resp) {
                  var root = resp.data && resp.data.accommodationSettings;
                  if (root && root.error) {
                      throw new Error(root.message || "Failed to load configuration");
                  }
                  var settings = root && root.data ? root.data : null;
                  return [configRowFromSettings(entity, campus, semesterFilter, settings)];
              })
        : fetchAllSettingsRows(apollo, entity, campus);

    var assignmentsPromise = fetchAllAssignments(apollo, entity, campus, semesterFilter || null);

    return Promise.all([settingsPromise, assignmentsPromise]).then(function (results) {
        var configRows = results[0] && results[0].length ? results[0] : [configRowFromSettings(entity, campus, semesterFilter, null)];
        var assignments = results[1];

        var assignmentRows = assignments.map(function (item) {
            return {
                Entity: item.entity || entity || "",
                Campus: item.campus || campus || "",
                Semester: item.semester || semesterFilter || "",
                Villa: num(item.villa),
                "Room no.": num(item.room_no),
                "Bed Slot": num(item.bed_slot),
                "Student ID": item.student_id || "",
                "Student Name": studentName(item),
                "Emirates ID": studentEid(item),
                Program: studentProgram(item),
                Sponser: item.sponsor || "",
                "Date Of Entry": item.date_of_entry || "",
            };
        });

        return [
            { name: "Configuration", rows: configRows },
            { name: "Accommodation", rows: assignmentRows },
        ];
    });
}

function fetchAllSettingsRows(apollo, entity, campus) {
    var limit = 100;
    var page = 1;
    var all = [];

    function next() {
        return apollo
            .query({
                query: SETTINGS_LIST_QUERY,
                variables: {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                },
                fetchPolicy: "network-only",
            })
            .then(function (resp) {
                var root = resp.data && resp.data.accommodationSettingsList;
                if (!root || root.error) {
                    throw new Error((root && root.message) || "Failed to load configuration");
                }
                var items = root.data || [];
                for (var i = 0; i < items.length; i++) {
                    all.push(configRowFromSettings(entity, campus, items[i].semester, items[i]));
                }
                var pi = root.paginatorInfo;
                if (pi && pi.currentPage < pi.lastPage && page < 50) {
                    page += 1;
                    return next();
                }
                return all;
            });
    }

    return next();
}

function fetchAllAssignments(apollo, entity, campus, semester) {
    var limit = 500;
    var page = 1;
    var all = [];

    function next() {
        return apollo
            .query({
                query: ASSIGNMENTS_QUERY,
                variables: {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    semester: semester || null,
                },
                fetchPolicy: "network-only",
            })
            .then(function (resp) {
                var root = resp.data && resp.data.accommodationAssignments;
                if (!root || root.error) {
                    throw new Error((root && root.message) || "Failed to load assignments");
                }
                var items = root.data || [];
                for (var i = 0; i < items.length; i++) {
                    all.push(items[i]);
                }
                var pi = root.paginatorInfo;
                if (pi && pi.currentPage < pi.lastPage && page < 100) {
                    page += 1;
                    return next();
                }
                return all;
            });
    }

    return next();
}
